import { Grid, Input, NativeSelect, Paper } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import MaterialTable from 'material-table';
import React, { useEffect, useMemo, useState } from 'react';
import Pagination from 'react-js-pagination';
import { Link, useHistory } from 'react-router-dom';

import Loading from '../../components/Loading';
import Header from '../../layout/Header';
import Subheader from '../../layout/Subheader';
import api from '../../services/api';
import { findPermission, TOKEN_PAGINATION } from '../../services/auth';
import { materialTableLocalizationPtBR } from '../../services/materialTableLocalization';

import './pagination.style.css';
import useStyles from './style';

export const MODULO_ID_BANCOS_REMESSAS = 11120;

const TIPO_PAGAMENTO_LABEL = { 1: 'TED', 2: 'PIX' };
const TIPO_LAYOUT_LABEL = { 1: 'CNAB 240', 2: 'CNAB 400' };
const STATUS_LABEL = { 0: 'Inativo', 1: 'Ativo' };

export function BancosRemessas() {
  const classes = useStyles();
  const history = useHistory();

  const [isLoading, setLoading] = useState(true);
  const [bancosRemessas, setBancosRemessas] = useState([]);
  const [bancos, setBancos] = useState([]);

  const [nomeInput, setNomeInput] = useState('');
  const [cnpjInput, setCnpjInput] = useState('');
  const [bancoIdInput, setBancoIdInput] = useState('');
  const [tipoPagamentoInput, setTipoPagamentoInput] = useState('');
  const [tipoLayoutInput, setTipoLayoutInput] = useState('');
  const [status, setStatus] = useState('Ativo');

  const [currentPage, setCurrentPage] = useState(1);
  const [itensPerPage, setItensPerPage] = useState(50);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    if (!findPermission(MODULO_ID_BANCOS_REMESSAS, 'Consultar')) {
      history.replace('/app/home');
    }
  }, [history]);

  const buildRows = (rows) =>
    rows.map((r) => ({
      ...r,
      bancoNome: bancos.find((b) => b.bancoId === r.bancoId)?.banco || `#${r.bancoId}`,
      tipoPagamentoLabel: TIPO_PAGAMENTO_LABEL[r.tipoPagamento] || r.tipoPagamento,
      tipoLayoutLabel: `${TIPO_LAYOUT_LABEL[r.tipoLayout] || ''} ${r.versaoLayout || ''}`.trim(),
      statusLabel: STATUS_LABEL[r.status] || r.status,
    }));

  const fetchFilterOptions = async () => {
    const res = await api.get('Bancos/Consultar?status=Ativo');
    setBancos(res?.data?.resposta || []);
  };

  const fetchBancosRemessas = async (pageNumber = currentPage) => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append('paginaInicial', pageNumber);
    params.append('itensPorPagina', itensPerPage);
    if (status) params.append('status', status);
    if (nomeInput) params.append('nome', nomeInput);
    if (cnpjInput) params.append('cnpj', cnpjInput);
    if (bancoIdInput) params.append('bancoId', bancoIdInput);
    if (tipoPagamentoInput) params.append('tipoPagamento', tipoPagamentoInput);
    if (tipoLayoutInput) params.append('tipoLayout', tipoLayoutInput);

    const res = await api.get(`BancosRemessas/Consultar?${params.toString()}`);
    setPagination(res?.data?.paginacao || {});
    setBancosRemessas(res?.data?.paginacao?.qtdTotalRegistros > 0 ? res.data.resposta : []);
    setLoading(false);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchBancosRemessas(pageNumber);
  };

  useEffect(() => {
    fetchFilterOptions();
    const userPage = JSON.parse(sessionStorage.getItem(TOKEN_PAGINATION))?.paginacao?.itensPorPagina;
    setItensPerPage(userPage === 0 || !userPage ? 50 : userPage);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    const delay = setTimeout(() => {
      fetchBancosRemessas(1);
    }, 800);
    return () => clearTimeout(delay);
  }, [nomeInput, cnpjInput, itensPerPage, status, bancoIdInput, tipoPagamentoInput, tipoLayoutInput]);

  const memoizedTable = useMemo(
    () => (
      <MaterialTable
        className={classes.table}
        options={{
          showTitle: false,
          search: false,
          toolbar: false,
          headerStyle: { backgroundColor: '#F5F5F5', textTransform: 'none', fontSize: '15px' },
          filtering: false,
          paging: false,
          showEmptyDataSourceMessage: false,
        }}
        localization={materialTableLocalizationPtBR}
        onRowClick={(event, rowData) => {
          if (!!findPermission(MODULO_ID_BANCOS_REMESSAS, 'Alterar')) {
            history.push(`/app/bancos-remessas/${rowData.bancoRemessaId}/editar`);
          }
        }}
        columns={[
          {
            title: 'Nome',
            field: 'nome',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
          },
          {
            title: 'Banco',
            field: 'bancoNome',
            cellStyle: { fontSize: '13px', padding: '8px 16px' },
          },
          {
            title: 'CNPJ',
            field: 'cnpj',
            cellStyle: { fontSize: '13px', padding: '8px 16px', width: '140px' },
          },
          {
            title: 'Tipo Pagto',
            field: 'tipoPagamentoLabel',
            cellStyle: { fontSize: '13px', padding: '8px 16px', width: '90px' },
          },
          {
            title: 'Layout',
            field: 'tipoLayoutLabel',
            cellStyle: { fontSize: '13px', padding: '8px 16px', width: '120px' },
          },
          {
            title: 'Status',
            field: 'statusLabel',
            cellStyle: { fontSize: '13px', padding: '8px 16px', width: '80px' },
          },
          {
            title: 'Ações',
            field: 'actions',
            headerStyle: { textAlign: 'center' },
            cellStyle: { fontSize: '13px', textAlign: 'center', padding: '8px 16px' },
            sorting: false,
            disableClick: true,
            render: (rowData) => (
              <>
                {!!findPermission(MODULO_ID_BANCOS_REMESSAS, 'Consultar') && (
                  <Link
                    className={classes.buttonVisualize}
                    to={`/app/bancos-remessas/${rowData.bancoRemessaId}/visualizar`}
                  >
                    <VisibilityIcon style={{ fontSize: 30, color: '#4AAF05' }} />
                  </Link>
                )}
                {!!findPermission(MODULO_ID_BANCOS_REMESSAS, 'Alterar') && (
                  <Link
                    className={classes.buttonEdit}
                    to={`/app/bancos-remessas/${rowData.bancoRemessaId}/editar`}
                  >
                    <EditIcon style={{ fontSize: 30, color: '#FB8832' }} />
                  </Link>
                )}
              </>
            ),
          },
        ]}
        data={buildRows(bancosRemessas)}
      />
    ),
    [bancosRemessas, bancos],
  );

  return (
    <div className={classes.root}>
      <Header />
      <Subheader
        title="Bancos Remessas"
        back="/app/home"
        create={
          !!findPermission(MODULO_ID_BANCOS_REMESSAS, 'Cadastrar')
            ? () => history.push('/app/bancos-remessas/cadastrar')
            : undefined
        }
      />
      <div className={classes.content}>
        <div className={classes.rootForm}>
          <header>
            <Grid container className={classes.searchContainer}>
              <Grid item xs={2} className={classes.item}>
                <label className={classes.label}>Nome</label>
                <Paper className={classes.medium} elevation={0}>
                  <Input
                    className={classes.input}
                    value={nomeInput}
                    placeholder="Nome"
                    onChange={(e) => setNomeInput(e.target.value)}
                  />
                </Paper>
              </Grid>
              <Grid item xs={2} className={classes.item}>
                <label className={classes.label}>CNPJ</label>
                <Paper className={classes.medium} elevation={0}>
                  <Input
                    className={classes.input}
                    value={cnpjInput}
                    placeholder="CNPJ"
                    onChange={(e) => setCnpjInput(e.target.value.replace(/\D/g, ''))}
                    inputProps={{ maxLength: 14 }}
                  />
                </Paper>
              </Grid>
              <Grid item xs={2} className={classes.item}>
                <label className={classes.label}>Banco</label>
                <NativeSelect
                  className={classes.input}
                  value={bancoIdInput}
                  onChange={(e) => setBancoIdInput(e.target.value)}
                  style={{ marginBottom: '15px', width: '95%' }}
                >
                  <option value="">Todos</option>
                  {bancos.map((b) => (
                    <option key={b.bancoId} value={b.bancoId}>
                      {b.banco}
                    </option>
                  ))}
                </NativeSelect>
              </Grid>
              <Grid item xs={2} className={classes.item}>
                <label className={classes.label}>Tipo Pagto</label>
                <NativeSelect
                  className={classes.input}
                  value={tipoPagamentoInput}
                  onChange={(e) => setTipoPagamentoInput(e.target.value)}
                  style={{ marginBottom: '15px', width: '95%' }}
                >
                  <option value="">Todos</option>
                  <option value="TED">TED</option>
                  <option value="PIX">PIX</option>
                </NativeSelect>
              </Grid>
              <Grid item xs={2} className={classes.item}>
                <label className={classes.label}>Layout</label>
                <NativeSelect
                  className={classes.input}
                  value={tipoLayoutInput}
                  onChange={(e) => setTipoLayoutInput(e.target.value)}
                  style={{ marginBottom: '15px', width: '95%' }}
                >
                  <option value="">Todos</option>
                  <option value="CNAB240">CNAB 240</option>
                  <option value="CNAB400">CNAB 400</option>
                </NativeSelect>
              </Grid>
              <Grid item xs={2} className={classes.item}>
                <label className={classes.label}>Status</label>
                <Paper className={classes.medium} elevation={0}>
                  <NativeSelect
                    className={classes.input}
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="Ativo">Ativo</option>
                    <option value="Inativo">Inativo</option>
                  </NativeSelect>
                </Paper>
              </Grid>
              <Grid
                item
                xs={2}
                className={classes.item}
                style={{
                  margin: '0 0 0 auto',
                  marginBottom: '19px',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  type="button"
                  className={classes.buttonFilter}
                  onClick={() => fetchBancosRemessas(1)}
                >
                  Aplicar Filtro
                </button>
              </Grid>
            </Grid>
          </header>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              {memoizedTable}
              <div className="footer-table">
                <Pagination
                  activePage={currentPage}
                  itemsCountPerPage={itensPerPage}
                  totalItemsCount={pagination.qtdTotalRegistros || 0}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                />
                <div className="select-page">
                  <label>Qtd. por página</label>
                  <NativeSelect
                    onChange={(e) => setItensPerPage(Number(e.currentTarget.value))}
                    value={itensPerPage}
                  >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                  </NativeSelect>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default BancosRemessas;
